// Auth Selectors
import { createFeatureSelector, createSelector } from '@ngrx/store';
import { AuthState } from './auth.state';
import { hasAdminRole } from '../../core/constants/roles.constant';

export const selectAuthState = createFeatureSelector<AuthState>('auth');

export const selectUser = createSelector(
  selectAuthState,
  (state) => state.user
);

export const selectUserRole = createSelector(
  selectUser,
  (user) => user?.role ?? []
);

/**
 * True if the current user holds at least one admin-like role
 * (any role containing "admin", case-insensitive). Sole RBAC gate for
 * granting access to the Console application.
 */
export const selectHasAllowedRole = createSelector(
  selectUserRole,
  (roles) => hasAdminRole(roles)
);


export const selectToken = createSelector(
  selectAuthState,
  (state) => state.token
);

export const selectRefreshToken = createSelector(
  selectAuthState,
  (state) => state.refreshToken
);

export const selectIsAuthenticated = createSelector(
  selectAuthState,
  (state) => state.isAuthenticated
);

export const selectAuthLoading = createSelector(
  selectAuthState,
  (state) => state.loading
);

export const selectAuthError = createSelector(
  selectAuthState,
  (state) => state.error
);

/**
 * Composite selector combining isAuthenticated + hasAllowedRole into a
 * single emission derived from one selectAuthState read.
 *
 * Why this exists: selectIsAuthenticated and selectHasAllowedRole are both
 * updated by the same loginSuccess/ssoLoginSuccess reducer action, but a
 * component subscribing to them as two separate `store.select()` streams
 * (e.g. via combineLatest) gets notified once per selector, in subscription
 * order — producing a transient [isAuthenticated: true, hasAllowedRole:
 * false] emission before the correct [true, true] settles, even though the
 * underlying state changed atomically. This selector reads both fields off
 * a single selectAuthState projection so consumers get one consistent
 * {isAuthenticated, hasAllowedRole} value per state change, eliminating the
 * glitch entirely (see sign-in.ts).
 */
export const selectSignInOutcome = createSelector(
  selectIsAuthenticated,
  selectHasAllowedRole,
  (isAuthenticated, hasAllowedRole) => ({ isAuthenticated, hasAllowedRole })
);

export const selectUserDisplayName = createSelector(
  selectUser,
  (user) => user ? `${user.firstName} ${user.lastName}`.trim() : ''
);

export const selectUserEmail = createSelector(
  selectUser,
  (user) => user?.email ?? null
);

export const selectUserInitials = createSelector(
  selectUser,
  (user) => {
    if (!user) return '';
    const first = user.firstName?.charAt(0) ?? '';
    const last = user.lastName?.charAt(0) ?? '';
    return `${first}${last}`.toUpperCase();
  }
);
